import { IApiError } from '../../../models/api';
import { ApiService } from '../../../services/api';
import { CONSTANTS } from '../../../utils/constants';
import { getErrorMessages } from '../../../utils/formatters';
import { generateAsyncActionNames } from '../../../utils/helpers';
import { AppDispatch } from '../../type';
import { TableActions } from '../table/table.actions';
import {
  AddVouchersParams,
  GetVouchersPayload,
  UpdateVouchersParams,
} from './type';

const { get, post, put, deleteApi } = ApiService;

enum VouchersEndpoints {
  GET_VOUCHERS = '/Voucher/GetVouchers',
  ADD_VOUCHERS = '/Voucher/AddVouchers',
  UPDATE_VOUCHER = '/Voucher/UpdateVoucher',
  REMOVE_VOUCHER = '/Voucher/RemoveVoucher',
}

enum VouchersActionsEnum {
  GET_VOUCHERS = 'GET_VOUCHERS',
  ADD_VOUCHERS = 'ADD_VOUCHERS',
  GET_VOUCHER = 'GET_VOUCHER',
  UPDATE_VOUCHER = 'UPDATE_VOUCHER',
  REMOVE_VOUCHER = 'REMOVE_VOUCHER',
}

export const VouchersActionsNames = {
  [VouchersActionsEnum.GET_VOUCHERS]: generateAsyncActionNames(
    VouchersActionsEnum.GET_VOUCHERS
  ),
  [VouchersActionsEnum.ADD_VOUCHERS]: generateAsyncActionNames(
    VouchersActionsEnum.ADD_VOUCHERS
  ),
  [VouchersActionsEnum.GET_VOUCHER]: generateAsyncActionNames(
    VouchersActionsEnum.GET_VOUCHER
  ),
  [VouchersActionsEnum.UPDATE_VOUCHER]: generateAsyncActionNames(
    VouchersActionsEnum.UPDATE_VOUCHER
  ),
  [VouchersActionsEnum.REMOVE_VOUCHER]: generateAsyncActionNames(
    VouchersActionsEnum.REMOVE_VOUCHER
  ),
};

const Actions = {
  [VouchersActionsEnum.GET_VOUCHERS]: {
    START: () => ({
      type: VouchersActionsNames.GET_VOUCHERS.START,
    }),
    FULFILLED: (data: GetVouchersPayload) => ({
      type: VouchersActionsNames.GET_VOUCHERS.FULFILLED,
      payload: data,
    }),
    REJECTED: (error: IApiError[] | null) => ({
      type: VouchersActionsNames.GET_VOUCHERS.REJECTED,
      payload: error,
    }),
  },
  [VouchersActionsEnum.ADD_VOUCHERS]: {
    START: () => ({
      type: VouchersActionsNames.ADD_VOUCHERS.START,
    }),
    FULFILLED: () => ({
      type: VouchersActionsNames.ADD_VOUCHERS.FULFILLED,
    }),
    REJECTED: (error: IApiError[] | null) => ({
      type: VouchersActionsNames.ADD_VOUCHERS.REJECTED,
      payload: error,
    }),
  },
  [VouchersActionsEnum.GET_VOUCHER]: {
    START: () => ({
      type: VouchersActionsNames.GET_VOUCHER.START,
    }),
    FULFILLED: (data: any) => ({
      type: VouchersActionsNames.GET_VOUCHER.FULFILLED,
      payload: data,
    }),
    REJECTED: (error: IApiError[] | null) => ({
      type: VouchersActionsNames.GET_VOUCHER.REJECTED,
      payload: error,
    }),
  },
  [VouchersActionsEnum.UPDATE_VOUCHER]: {
    START: () => ({
      type: VouchersActionsNames.UPDATE_VOUCHER.START,
    }),
    FULFILLED: () => ({
      type: VouchersActionsNames.UPDATE_VOUCHER.FULFILLED,
    }),
    REJECTED: (error: IApiError[] | null) => ({
      type: VouchersActionsNames.UPDATE_VOUCHER.REJECTED,
      payload: error,
    }),
  },
  [VouchersActionsEnum.REMOVE_VOUCHER]: {
    START: () => ({
      type: VouchersActionsNames.REMOVE_VOUCHER.START,
    }),
    FULFILLED: () => ({
      type: VouchersActionsNames.REMOVE_VOUCHER.FULFILLED,
    }),
    REJECTED: (error: IApiError[] | null) => ({
      type: VouchersActionsNames.REMOVE_VOUCHER.REJECTED,
      payload: error,
    }),
  },
};

const assertSuccess = (data: any) => {
  if (!data?.isSucceeded) {
    throw Error(JSON.stringify(data?.errors));
  }
};

const getVouchers =
  (
    params?: any,
    sort?: any,
    updateTable = true,
    pageNumber: number = CONSTANTS.GRIDS.FIRST_PAGE_NUMBER,
    pageSize?: number | null
  ) =>
  async (dispatch: AppDispatch, getState: any) => {
    dispatch(Actions[VouchersActionsEnum.GET_VOUCHERS].START());

    const requestData: any = {
      params,
      headers: {
        'X-Pagination': JSON.stringify({
          PageNumber: pageNumber,
          PageSize:
            pageSize ?? getState()?.table.pageSize ?? CONSTANTS.GRIDS.PAGE_SIZE,
        }),
      },
    };

    if (sort && sort.length) {
      requestData.headers['X-Sorting'] = JSON.stringify({ Sortings: sort });
    }

    try {
      const { data }: any = await get(
        `${VouchersEndpoints.GET_VOUCHERS}`,
        requestData
      );

      const vouchers = data?.content?.vouchers ?? [];

      dispatch(Actions[VouchersActionsEnum.GET_VOUCHERS].FULFILLED(vouchers));

      if (updateTable) {
        dispatch(
          TableActions.setTableData({
            totalRows: data?.content?.totalCount ?? vouchers.length,
            pageNumber: CONSTANTS.GRIDS.FIRST_PAGE_NUMBER,
          })
        );
      }
      return vouchers;
    } catch (error: any) {
      dispatch(
        Actions[VouchersActionsEnum.GET_VOUCHERS].REJECTED(
          getErrorMessages(error)
        )
      );
    }
  };

const addVouchers =
  (body: AddVouchersParams) => async (dispatch: AppDispatch) => {
    dispatch(Actions[VouchersActionsEnum.ADD_VOUCHERS].START());
    try {
      const { data }: any = await post(VouchersEndpoints.ADD_VOUCHERS, body);
      assertSuccess(data);
      dispatch(Actions[VouchersActionsEnum.ADD_VOUCHERS].FULFILLED());
      return data?.content;
    } catch (error: any) {
      dispatch(
        Actions[VouchersActionsEnum.ADD_VOUCHERS].REJECTED(
          getErrorMessages(error)
        )
      );
    }
  };

const getVoucherById = (id: number) => async (dispatch: AppDispatch) => {
  dispatch(Actions[VouchersActionsEnum.GET_VOUCHER].START());
  try {
    const params = new URLSearchParams();
    params.append('voucherId', String(id));
    const { data }: any = await get(VouchersEndpoints.GET_VOUCHERS, { params });
    assertSuccess(data);
    const voucher = data?.content?.vouchers?.[0] ?? null;
    if (!voucher) {
      throw Error('Voucher not found');
    }
    dispatch(Actions[VouchersActionsEnum.GET_VOUCHER].FULFILLED(voucher));
    return voucher;
  } catch (error: any) {
    dispatch(
      Actions[VouchersActionsEnum.GET_VOUCHER].REJECTED(getErrorMessages(error))
    );
  }
};

const updateVoucher =
  (id: number, body: UpdateVouchersParams) => async (dispatch: AppDispatch) => {
    dispatch(Actions[VouchersActionsEnum.UPDATE_VOUCHER].START());
    try {
      const { data }: any = await put(
        `${VouchersEndpoints.UPDATE_VOUCHER}/${id}`,
        body
      );
      assertSuccess(data);
      dispatch(Actions[VouchersActionsEnum.UPDATE_VOUCHER].FULFILLED());
      return data?.content;
    } catch (error: any) {
      dispatch(
        Actions[VouchersActionsEnum.UPDATE_VOUCHER].REJECTED(
          getErrorMessages(error)
        )
      );
    }
  };

const removeVoucher = (id: number) => async (dispatch: AppDispatch) => {
  dispatch(Actions[VouchersActionsEnum.REMOVE_VOUCHER].START());
  try {
    const { data }: any = await deleteApi(
      `${VouchersEndpoints.REMOVE_VOUCHER}/${id}`
    );
    assertSuccess(data);
    dispatch(Actions[VouchersActionsEnum.REMOVE_VOUCHER].FULFILLED());
  } catch (error: any) {
    dispatch(
      Actions[VouchersActionsEnum.REMOVE_VOUCHER].REJECTED(
        getErrorMessages(error)
      )
    );
  }
};

export const VouchersActions = {
  getVouchers,
  addVouchers,
  getVoucherById,
  updateVoucher,
  removeVoucher,
};
